import { useCallback } from 'react';
import { PREFERENCE_TYPE_EMAIL_MARKETING, PREFERENCE_TYPE_DATA_PRIVACY } from '../constants';

export const STORAGE_KEY_PREFIX = 'lm_consent_prefs_';

export type PreferenceType =
  | typeof PREFERENCE_TYPE_EMAIL_MARKETING
  | typeof PREFERENCE_TYPE_DATA_PRIVACY;

export interface CachedPreferenceEntry {
  value: boolean;
  pendingSync: boolean;
}

export type CachedPreferences = {
  [K in PreferenceType]?: CachedPreferenceEntry;
};

const useConsentPreferencesCache = (userId: string | undefined) => {
  const storageKey = userId ? `${STORAGE_KEY_PREFIX}${userId}` : null;

  const getCached = useCallback((): CachedPreferences | null => {
    if (!storageKey || typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      // Callers must treat this as best-effort — no runtime shape validation beyond JSON.parse.
      // A malformed entry is handled defensively by each consumer (e.g. clearPending type-checks entry.value).
      return JSON.parse(raw) as CachedPreferences;
    } catch {
      return null;
    }
  }, [storageKey]);

  const updateCache = useCallback(
    (type: PreferenceType, value: boolean, pendingSync: boolean): void => {
      if (!storageKey || typeof window === 'undefined') return;
      try {
        const current = getCached() ?? {};
        localStorage.setItem(
          storageKey,
          JSON.stringify({ ...current, [type]: { value, pendingSync } }),
        );
      } catch {
        // fail silently — localStorage unavailable or quota exceeded
      }
    },
    [storageKey, getCached],
  );

  const clearPending = useCallback(
    (type: PreferenceType): void => {
      if (!storageKey || typeof window === 'undefined') return;
      try {
        // Intentional: delegates to updateCache (which calls getCached internally) to keep write logic in one place
        const current = getCached();
        const entry = current?.[type];
        // Guard against malformed cache entries (e.g. from stale schema)
        if (!entry || typeof entry.value !== 'boolean') return;
        updateCache(type, entry.value, false);
      } catch {
        // fail silently
      }
    },
    [storageKey, getCached, updateCache],
  );

  return { getCached, updateCache, clearPending };
};

export default useConsentPreferencesCache;
